import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
// Не из индекса фичи: он тянет экран игры (отдельный чанк). Здесь — данные хаба, старт миссии и кнопка с ценой.
import { useStartRunMutation, useStoriesHubQuery } from '@features/Stories/model/queries';
import { storyError } from '@features/Stories/ui/parts';
import { PlayButton } from '@features/Stories/ui/PlayButton';
import { trackEvent } from '@shared/analytics';
import { buildStoryPlayPath, paths } from '@shared/config';
import { cn } from '@shared/lib';
import { Button, RewardBadge, TokenIcon, toast } from '@shared/ui';

const MissionPortrait = lazy(() => import('./MissionPortrait'));

/**
 * «Миссия дня» — строка плашки «На сегодня» на главной (решение владельца 2026-10-23: задание и миссия в одной плашке,
 * пиксель-арт игры): портрет собеседника миссии, что дают жетонами, название и кнопка. Начать — сразу в игру (миссия
 * бесплатна), начатую — продолжить, пройденную — полученные жетоны или итоги. Хаб не загрузился — строки нет.
 */
export function MissionRow() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hub = useStoriesHubQuery().data;
  const start = useStartRunMutation();
  if (!hub) return null;

  const daily = hub.daily;
  const runId = daily.run_id;
  const active = daily.status === 'active' && !!runId;
  const finished = daily.status === 'finished' || daily.status === 'abandoned';
  const stepsDone = daily.steps.filter((s) => s.done).length;

  const play = () => {
    trackEvent('home_mission_clicked', { action: active ? 'resume' : 'start' });
    if (active) {
      navigate(buildStoryPlayPath(runId));
      return;
    }
    start.mutate(
      { kind: 'daily' },
      {
        onSuccess: (run) => navigate(buildStoryPlayPath(run.id)),
        onError: (err) => {
          const info = storyError(err);
          toast({
            tone: 'error',
            message: info.code === 'STORY_NO_TOKENS' ? t('stories.play.errors.noTokens') : (info.message ?? t('stories.play.errors.generic')),
          });
        },
      },
    );
  };

  // Метка у надзаголовка: что дадут, сколько шагов пройдено или что жетонов не дали (пройдена с ошибками в целях).
  const noReward = finished && !daily.granted && !daily.done;
  const pill: { text: string; tone: 'accent' | 'muted' } | null = finished
    ? noReward
      ? { text: t('dashboard.home.mission.noReward'), tone: 'muted' }
      : null
    : active
      ? { text: t('dashboard.home.dailies.steps', { n: stepsDone, total: daily.steps.length }), tone: 'accent' }
      : { text: t('dashboard.home.mission.upTo', { count: daily.reward.perfect }), tone: 'accent' };

  return (
    <div className="dl-row flex items-center gap-3">
      <span aria-hidden className="dl-art lx-px-sm grid h-12 w-12 flex-none place-items-center overflow-hidden bg-[#3f86d9]">
        <Suspense fallback={null}>
          <MissionPortrait character={daily.character} className="h-12 w-12" />
        </Suspense>
      </span>
      <div className="dl-text min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] font-semibold uppercase tracking-[0.12em] text-muted">
          {t('stories.daily.kicker')}
          {pill && (
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-pill px-1.5 py-0.5 text-[11px] font-semibold normal-case tracking-normal',
                pill.tone === 'accent' ? 'bg-accent/15 text-accent' : 'bg-white/[0.08] text-muted',
              )}
            >
              <TokenIcon size={12} className={pill.tone === 'muted' ? 'opacity-50 grayscale' : undefined} />
              {pill.text}
            </span>
          )}
        </div>
        <h3 className="mt-0.5 line-clamp-2 break-words font-serif text-[17px] font-medium leading-[22px] text-fg">{daily.title}</h3>
      </div>
      <div className="dl-action flex flex-none items-center gap-2">
        {active ? (
          <Button variant="outline" size="sm" onClick={play}>
            {t('dashboard.home.dailies.resume')}
          </Button>
        ) : finished ? (
          daily.granted ? (
            <RewardBadge size="sm" amount={daily.granted} caption={t('rewards.earned', { count: daily.granted })} />
          ) : (
            runId && (
              <Button variant="ghost" size="sm" onClick={() => navigate(buildStoryPlayPath(runId))}>
                {t('dashboard.home.dailies.result')}
              </Button>
            )
          )
        ) : (
          <PlayButton
            label={t('dashboard.home.dailies.start')}
            price={hub.prices.daily}
            busy={start.isPending}
            busyLabel={t('dashboard.home.dailies.starting')}
            onClick={play}
            onPlans={() => navigate(paths.plans)}
            size="sm"
            variant="outline"
          />
        )}
      </div>
    </div>
  );
}
